'use client';

/**
 * MessagesSection - Message thread display for Chat tab
 *
 * Renders chronological message list from Odoo chatter using the
 * copilot message bubble pattern (component-inventory #9).
 * Strips HTML tags from Odoo chatter bodies into readable plain text.
 *
 * Schema-driven: rendered when CHAT_SCHEMA includes type='messages'.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot
 */

import { useState } from 'react';
import { FileText, Globe, Image, MessageSquare, Paperclip, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PlatformBadge } from '../../details/shared';
import type { MessageItem } from '@/lib/api/types';

// ============================================================
// Helpers
// ============================================================

/** Strip HTML tags from Odoo chatter messages into readable plain text. */
function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '  \u2022 ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const AVATAR_COLORS = [
  'bg-primary/15 text-primary',
  'bg-success/15 text-success',
  'bg-warning/15 text-warning',
  'bg-info/15 text-info',
  'bg-destructive/15 text-destructive',
];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Parse attachment metadata JSON and match with URLs */
function parseAttachments(urls: string[] | null, metadataJson: string | null) {
  if (!urls || urls.length === 0) return [];
  let metadata: Array<{ filename: string; mimetype: string; size: number }> = [];
  if (metadataJson) {
    try { metadata = JSON.parse(metadataJson); } catch { /* fallback to URL names */ }
  }
  return urls.map((url, i) => {
    const meta = metadata[i] || { filename: '', mimetype: '', size: 0 };
    return {
      url,
      filename: meta.filename || url.split('/').pop() || 'attachment',
      mimetype: meta.mimetype || 'application/octet-stream',
      size: meta.size || 0,
    };
  });
}

function getFileIcon(mimetype: string) {
  if (mimetype.startsWith('image/')) return Image;
  if (mimetype === 'application/pdf') return FileText;
  return Paperclip;
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ============================================================
// Source badge — compact origin label for aggregated threads
// ============================================================

const SOURCE_CONFIG: Record<string, { color: string; label: (name?: string | null) => string }> = {
  project: { color: 'bg-primary/10 text-primary', label: () => 'Project' },
  rfq:     { color: 'bg-info/10 text-info',       label: (n) => `RFQ: ${n || 'Unknown'}` },
  purchase_order: { color: 'bg-success/10 text-success', label: (n) => `PO: ${n || 'Unknown'}` },
};

// ============================================================
// MessageBubble — copilot variant (component-inventory #9)
// ============================================================

function MessageBubble({ message, isLast }: { message: MessageItem; isLast: boolean }) {
  const authorName = message.author || message.author_name || 'Unknown';
  const initial = authorName[0].toUpperCase();
  const rawDate = message.date || message.created_date;
  const originalBody = stripHtml(message.body);
  const translatedBody = message.body_translated ? stripHtml(message.body_translated) : null;
  const hasTranslation = !!translatedBody;
  const [showOriginal, setShowOriginal] = useState(false);
  const body = hasTranslation && !showOriginal ? translatedBody : originalBody;
  const attachments = parseAttachments(message.attachment_urls, message.attachment_metadata);
  const config = SOURCE_CONFIG[message.source] || SOURCE_CONFIG.project;

  return (
    <div
      className={cn(
        'flex gap-3 px-3 py-3 hover:bg-accent/30 transition-colors',
        !isLast && 'border-b border-border/50',
      )}
    >
      {/* Avatar */}
      <div className={cn(
        'w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[10px] font-semibold',
        avatarColor(authorName),
      )}>
        {initial}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-medium truncate">{authorName}</span>
          {message.platform && <PlatformBadge platform={message.platform} />}
          <Badge variant="secondary" className={cn('text-[9px] px-1 py-0', config.color)}>
            {config.label(message.source_name || message.target_label)}
          </Badge>
          {hasTranslation && (
            <button
              onClick={() => setShowOriginal(!showOriginal)}
              className="inline-flex items-center gap-0.5 text-[9px] text-muted-foreground hover:text-foreground transition-colors"
              title={showOriginal ? 'View translation' : 'View original'}
            >
              <Globe className="h-2.5 w-2.5" />
              {showOriginal ? message.language?.toUpperCase() : message.translated_to?.toUpperCase()}
            </button>
          )}
          {rawDate && (
            <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
              {formatTimestamp(rawDate)}
            </span>
          )}
        </div>
        {body && (
          <p className="text-xs text-foreground whitespace-pre-wrap break-words leading-relaxed">
            {body}
          </p>
        )}
        {attachments.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {attachments.map((att, i) => {
              const Icon = getFileIcon(att.mimetype);
              return (
                <a
                  key={i}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[10px] text-primary hover:underline bg-primary/5 border border-primary/20 rounded-lg px-2 py-1 transition-colors hover:bg-primary/10"
                >
                  <Icon className="h-3 w-3 shrink-0" />
                  <span className="truncate max-w-[120px]">{att.filename}</span>
                  {att.size > 0 && (
                    <span className="text-muted-foreground shrink-0">{formatSize(att.size)}</span>
                  )}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

interface MessagesSectionProps {
  messages: MessageItem[];
  isLoading?: boolean;
  onRefresh?: () => void;
  header?: string;
  metadata?: {
    message_count?: number;
    rfq_count?: number;
    po_count?: number;
  } | null;
}

export function MessagesSection({
  messages,
  isLoading = false,
  onRefresh,
  header = 'MESSAGES',
  metadata,
}: MessagesSectionProps) {
  // Sort messages by date (newest first)
  const sortedMessages = [...messages].sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0;
    const dateB = b.date ? new Date(b.date).getTime() : 0;
    return dateB - dateA;
  });

  const stats = metadata
    ? [
        { label: 'messages', value: metadata.message_count },
        { label: 'RFQs', value: metadata.rfq_count },
        { label: 'POs', value: metadata.po_count },
      ].filter((s) => s.value !== undefined)
    : [];

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm flex items-center justify-between px-3 py-2.5 border-b border-border">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">{header}</span>
          {stats.length > 0 && (
            <span className="text-[10px] text-muted-foreground">
              {stats.map((s) => `${s.value} ${s.label}`).join(' \u00b7 ')}
            </span>
          )}
        </div>
        {onRefresh && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onRefresh}
            disabled={isLoading}
            title="Refresh messages"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isLoading && 'animate-spin')} />
          </Button>
        )}
      </div>

      {/* Messages */}
      {sortedMessages.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center text-muted-foreground py-8">
          <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
          <p className="text-sm">No messages yet</p>
        </div>
      ) : (
        sortedMessages.map((msg, idx) => (
          <MessageBubble
            key={msg.cdk ?? idx}
            message={msg}
            isLast={idx === sortedMessages.length - 1}
          />
        ))
      )}
    </div>
  );
}
